import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useCurrentUser } from '@hooks/auth/useCurrentUser';
import { useIsSuperAdmin } from '@hooks/auth/useIsSuperAdmin';
import { useIsVendor } from '@hooks/auth/useIsVendor';
import { useIsMerchant } from '@hooks/auth/useIsMerchant';

/**
 * Attaches the signed-in user's identity to Sentry (port of the legacy
 * `Sentry.setUser` / `Sentry.setTag` wiring) so error reports and session
 * replays carry who hit them. Clears the user on logout. Renders nothing.
 *
 * No-op in environments where Sentry isn't initialized (dev/local) —
 * `setUser` / `setTag` are safe to call regardless. The `user_type` tag mirrors
 * the GoogleAnalytics mapping (super-admin / vendor / merchant).
 */
export function SentryUser(): null {
  const user = useCurrentUser();
  const isSuperAdmin = useIsSuperAdmin();
  const isVendor = useIsVendor();
  const isMerchant = useIsMerchant();

  useEffect(() => {
    if (!user?.id) {
      Sentry.setUser(null);
      // Clear the lingering tag too, so post-logout events aren't attributed
      // to the previous user's type.
      Sentry.setTag('user_type', undefined);
      return;
    }
    const userType = isSuperAdmin
      ? 'super-admin'
      : isVendor
        ? 'vendor'
        : isMerchant
          ? 'merchant'
          : '';
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
    });
    Sentry.setTag('user_type', userType);
  }, [user?.id, user?.email, user?.firstName, user?.lastName, isSuperAdmin, isVendor, isMerchant]);

  return null;
}
